import { useRef } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { BiErrorCircle } from 'react-icons/bi';

// Icon component
const Icon = ({ isOpen, onClick = () => {} }) => {
  return (
    <svg
      viewBox='0 0 24 24'
      width='18'
      height='18'
      stroke='#222'
      strokeWidth='1.5'
      fill='none'
      strokeLinecap='round'
      strokeLinejoin='round'
      className={isOpen ? 'translate' : ''}
      onClick={onClick}
    >
      <polyline points='6 9 12 15 18 9'></polyline>
    </svg>
  );
};

// CloseIcon component
const CloseIcon = ({ onClick = () => {} }) => {
  return (
    <svg
      viewBox='0 0 24 24'
      width='14'
      height='14'
      stroke='#000'
      strokeWidth='2'
      fill='none'
      strokeLinecap='round'
      strokeLinejoin='round'
      onClick={onClick}
    >
      <line x1='18' y1='6' x2='6' y2='18'></line>
      <line x1='6' y1='6' x2='18' y2='18'></line>
    </svg>
  );
};

/*
  INPUTS:
  -------
  options = [{label:'Value A', value:'a'}, {label:'Value B', value:'b'}, {label:'Value C', value:'c'}]
  defaultselection = [{label:'Value A', value:'a'}] - for both single & multi provide as list

  loader={true/false}
  loadoptionseverytime={true}
  loadoptionfunc={() => fetchOptions(x)}

  OUTPUTS:
  --------
  onChange will receive list of values
  single - [{label:'Value A', value:'a'}]
  multi - [{label:'Value A', value:'a'}, {label:'Value B', value:'b'}]
*/
const Select = ({
  options = [],
  loadoptionseverytime = false,
  loadoptionfunc,
  loader = false,
  placeholder = '-- select an option --',
  isMulti = false,
  isSearchable = true,
  onChange,
  defaultselection = [],
  name,
  label,
  error,
  grouptext,
  icon,
  iconclick,
  iconposition = 'left',
  iconclass = '',
  customclass = '',
  keyvalue = '',
  allclose = false
}) => {
  const [showMenu, setShowMenu] = useState(false); // Controls the visibility of the dropdown menu
  const [menuoptions, setMenuOptions] = useState([]); // Controls the visibility of the dropdown menu
  const [selectedValue, setSelectedValue] = useState(isMulti ? [] : null); // Stores the selected value(s)
  const [searchValue, setSearchValue] = useState(''); // Stores the value entered in the search input
  const searchRef = useRef(); // Reference to the search input element
  const inputRef = useRef(); // Reference to the custom select input element

  //updating options & default selection
  useEffect(() => {
    if (defaultselection.length > 0) {
      if (isMulti) {
        let selection = [];
        defaultselection.forEach((element) => {
          selection.push(element.value);
        });
        setSelectedValue(defaultselection);
        setMenuOptions(options.filter((x) => !selection.includes(x.value)));
      } else {
        setSelectedValue(defaultselection);
        setMenuOptions(options);
      }
    } else {
      setMenuOptions(options);
      setSelectedValue([]);
    }
  }, [options, isMulti, defaultselection]);

  //show search box in menu options
  useEffect(() => {
    setSearchValue('');
    if (showMenu && searchRef.current) {
      searchRef.current.focus();
    }
  }, [showMenu]);

  //show options in menu options
  const getDisplay = () => {
    if (!selectedValue || selectedValue.length === 0) {
      return placeholder;
    }
    if (isMulti) {
      return (
        <div className='dropdown-tags'>
          {selectedValue.map((option, index) => (
            <div key={`${option.value}-${index}`} className='dropdown-tag-item'>
              <span>{option.label}</span>
              <span
                onClick={(e) => onTagRemove(e, option)}
                className='dropdown-tag-close'
              >
                <CloseIcon />
              </span>
            </div>
          ))}
        </div>
      );
    }
    return selectedValue[0].label;
  };

  // remove options from menu options (used in multi select)
  const removeOption = (option) => {
    return selectedValue.filter((o) => o.value !== option.value);
  };

  // remove one option in selected options (used in multi select)
  const onTagRemove = (e, option) => {
    setMenuOptions([option, ...menuoptions]);
    e.stopPropagation();
    const newValue = removeOption(option);
    setSelectedValue(newValue);
    onChange(newValue);
  };

  const onItemClick = (option) => {
    let newValue;
    if (isMulti) {
      if (selectedValue.findIndex((o) => o.value === option.value) >= 0) {
        newValue = removeOption(option);
      } else {
        newValue = [...selectedValue, option];
      }
      setMenuOptions(menuoptions.filter((o) => o.value !== option.value));
    } else {
      newValue = [option];
    }
    setSelectedValue(newValue);
    onChange(newValue);
    if (!isMulti) setShowMenu(!showMenu);
  };

  const isSelected = (option) => {
    if (isMulti)
      return selectedValue.filter((o) => o.value === option.value).length > 0;
    if (!selectedValue || selectedValue.length === 0) return '';
    return selectedValue[0].value === option.value;
  };

  const onSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const getOptions = () => {
    if (!searchValue) return menuoptions;
    return menuoptions.filter(
      (option) =>
        option.label.toLowerCase().indexOf(searchValue.toLowerCase()) >= 0
    );
  };

  const getDropdown = () => {
    if (!showMenu && loadoptionseverytime) {
      loadoptionfunc();
    }
    setShowMenu(!showMenu);
  };

  return (
    <div className={`${customclass}`}>
      {label && (
        <label htmlFor={name} style={{ color: '#0678e6', fontWeight: 600 }}>
          {label}
        </label>
      )}
      <div className='cutom-selector input-group'>
        {/* Icon Display */}
        {icon && iconposition === 'left' && (
          <div className={`form-icon ${iconclass}`} onClick={iconclick}>
            {icon}
          </div>
        )}
        {/* Custom Select Box Display - Visible */}
        <div className='select-box' ref={inputRef}>
          <span
            className='flex-fill'
            style={{
              color:
                !selectedValue || selectedValue.length === 0 ? '#acacac' : ''
            }}
            onClick={getDropdown}
          >
            {getDisplay()}
          </span>
          {allclose &&
            (!isMulti
              ? selectedValue
                ? Object.keys(selectedValue).length > 0
                : false
              : selectedValue.length > 0) && (
              <CloseIcon
                onClick={() => {
                  setSelectedValue(isMulti ? [] : null);
                  onChange(isMulti ? [] : null);
                }}
              />
            )}
          <Icon isOpen={showMenu} onClick={getDropdown} />
        </div>
        {loader && showMenu && (
          <div className='select-options'>
            <div className='options-holder'>
              <div className='options-value'>Loading...</div>
            </div>
          </div>
        )}
        {!loader && showMenu && (
          <>
            <div className='select-options'>
              {isSearchable && menuoptions.length !== 0 && (
                <div className='search-box'>
                  <input
                    className='form-control'
                    onChange={onSearch}
                    value={searchValue}
                    ref={searchRef}
                  />
                </div>
              )}
              <div className='options-holder'>
                {getOptions().map((x) => {
                  return (
                    <div
                      key={keyvalue.length > 0 ? x[keyvalue] : x.label}
                      className={`options-value ${isSelected(x) && 'selected'}`}
                      onClick={() => onItemClick(x)}
                    >
                      {keyvalue.length > 0 ? x[keyvalue] : x.label}
                    </div>
                  );
                })}
                {menuoptions.length === 0 && (
                  <div className='options-value'>No option available</div>
                )}
              </div>
            </div>
            <div
              className='options-backdrop'
              onClick={() => setShowMenu(false)}
            ></div>
          </>
        )}
        {/* Icon Display */}
        {icon && iconposition !== 'left' && (
          <div className={`form-icon ${iconclass}`} onClick={iconclick}>
            {icon}
          </div>
        )}
        {/* Select Group Display */}
        {grouptext && (
          <span className='input-group-text' id='basic-addon2'>
            {grouptext}
          </span>
        )}
      </div>
      {error && (
        <div className='alert-message'>
          <BiErrorCircle /> {`  ${error}`}
        </div>
      )}
    </div>
  );
};

export default Select;
